import React from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import ImageGallery from '../ImageUpload/ImageGallery';
import ImageViewer from '../ImageUpload/ImageViewer';
import ImageAnalytics from '../Analytics/ImageAnalytics';
import { getMaxImagesByTier } from '../../utils/imageUtils';
import { getProfileImageWithFallback } from '../../utils/profileImageUtils';
import { getTypeConfig } from '../../utils/typeUtils';
import BookingModal from '../Booking/BookingModal';
import GiftButton from '../Gift/GiftButton';
import GiftDisplay from '../Gift/GiftDisplay';
import ReviewList from '../Review/ReviewList';

const GirlProfile = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [girl, setGirl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshImages, setRefreshImages] = React.useState(0);
  const [showBookingModal, setShowBookingModal] = React.useState(false);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = React.useState(false);

  React.useEffect(() => {
    const fetchGirl = async () => {
      try {
        if (!id) {
          console.error('No girl ID provided');
          setLoading(false);
          return;
        }
        
        const girlRef = doc(db, 'girls', id);
        const snap = await getDoc(girlRef);
        if (snap.exists()) {
          const girlData = { id: snap.id, ...snap.data() };
          
          // ดึงข้อมูล tier จาก users collection
          try {
            const userRef = doc(db, 'users', id);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              girlData.tier = userData.tier || 'free';
              girlData.tierExpiryDate = userData.tierExpiryDate;
              girlData.tierUpdatedAt = userData.tierUpdatedAt;
            }
          } catch (userError) {
            console.log('Could not fetch user tier data:', userError);
            girlData.tier = 'free'; // default
          }
          
          console.log('Girl data loaded:', girlData);
          setGirl(girlData);
        } else {
          console.error('Girl document does not exist:', id);
        }
      } catch (error) {
        console.error('Error fetching girl:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGirl();
  }, [id, refreshImages]);

  // ตรวจสอบว่า user เป็นเจ้าของโปรไฟล์หรือไม่
  const isOwner = currentUser && girl && currentUser.uid === girl.userId;
  
  // คำนวณจำนวนรูปที่สามารถอัพโหลดได้ตาม Tier
  const maxImages = isOwner ? getMaxImagesByTier(girl?.tier || 'free', 'girl') : 0;
  
  // ฟังก์ชันคำนวณราคาหลังส่วนลด
  const calculateDiscountedPrice = (originalPrice, discountType, discountValue) => {
    if (discountType === 'none' || discountValue <= 0) {
      return originalPrice;
    }
    
    if (discountType === 'fixed') {
      return Math.max(0, originalPrice - discountValue);
    }
    
    if (discountType === 'percentage') {
      const discountAmount = (originalPrice * discountValue) / 100;
      return Math.max(0, originalPrice - discountAmount);
    }
    
    return originalPrice;
  };

  // ฟังก์ชันสำหรับรีเฟรชรูปภาพ
  const handleImageUpdate = () => {
    setRefreshImages(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }
  
  if (!girl) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-2">ไม่พบข้อมูลน้อง</div>
          <div className="text-sm text-gray-500">กรุณาตรวจสอบ URL หรือลองใหม่อีกครั้ง</div>
        </div>
      </div>
    );
  }

  // ดึงรูปภาพหลัก
  const profileImage = getProfileImageWithFallback(girl, '/default-avatar.png');

  // ใช้ typeUtils แทนการกำหนดเอง

  const skinToneIcons = {
    fair: '⚪',
    light: '🟡',
    medium: '🟤',
    dark: '⚫'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Hero Section */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
          {/* Header with Gradient Background */}
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 p-8 text-white relative">
            <div className="absolute inset-0 bg-black opacity-20"></div>
            <div className="relative z-10 text-center">
              <div className="relative inline-block mb-6">
                <img
                  src={profileImage}
                  alt={girl.nickname}
                  className="w-40 h-40 object-cover rounded-full border-6 border-white shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiBmaWxsPSIjRkZCNkMxIi8+Cjx0ZXh0IHg9IjY0IiB5PSI3MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjMzMzIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5BdmF0YXI8L3RleHQ+Cjwvc3ZnPgo=';
                  }}
                />
                {girl.profileImage && (
                  <div className="absolute -top-2 -right-2 bg-yellow-400 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                    ⭐ รูปหลัก
                  </div>
                )}
                {/* Status Badge */}
                <div className={`absolute -bottom-2 -left-2 px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                  girl.status === 'available' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {girl.status === 'available' ? '✅ พร้อมรับงาน' : '❌ ไม่รับงาน'}
                </div>
              </div>
              
              <h1 className="text-4xl font-bold mb-2">Hugkan - {girl.nickname}</h1>
              {girl.bio && (
                <p className="text-xl text-pink-100 mb-4">{girl.bio}</p>
              )}
              
              {/* Quick Stats */}
              <div className="flex justify-center space-x-6 mt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {Array.from({ length: girl.grades || 4 }, (_, i) => '⭐').join('')}
                  </div>
                  <div className="text-sm text-pink-100">เกรด {girl.grades || 4}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{girl.height} cm</div>
                  <div className="text-sm text-pink-100">ส่วนสูง</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{girl.weight} kg</div>
                  <div className="text-sm text-pink-100">น้ำหนัก</div>
                </div>
              </div>
              
              {/* Plan Package */}
              <div className="mt-6 text-center">
                <div className="inline-block bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-yellow-300 text-lg">💎</span>
                    <span className="text-white font-semibold">
                      Package: {girl.tier === 'free' ? 'FREE' : girl.tier?.toUpperCase() || 'FREE'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Image Gallery/Viewer */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            📸 รูปภาพ
          </h2>
          {isOwner ? (
            <ImageGallery
              images={girl.images || []}
              pendingImages={girl.pendingImages || []}
              rejectedImages={girl.rejectedImages || []}
              girlId={girl.id}
              maxImages={maxImages}
              isOwner={isOwner}
              onImageUpdate={handleImageUpdate}
              girlData={girl}
            />
          ) : (
            <ImageViewer
              images={girl.images || []}
              girlId={girl.id}
              girlName={girl.nickname}
              onViewLimitReached={() => {
                alert('คุณดูรูปครบจำนวนที่กำหนดแล้ว กรุณาอัพเกรด Tier เพื่อดูรูปเพิ่มเติม');
              }}
              onPurchaseRequired={(image) => {
                alert(`ต้องการซื้อดูรูป ${image.originalName || 'รูปภาพ'} ด้วย T Coins`);
              }}
            />
          )}
        </div>

        {/* Detailed Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Basic Info Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              👤 ข้อมูลพื้นฐาน
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-medium">ประเภทเอ็น</span>
                <span className="text-lg font-semibold">
                  {(() => {
                    const typeInfo = getTypeConfig(girl.type);
                    return `${typeInfo.icon} ${typeInfo.label}`;
                  })()}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-medium">สีผิว</span>
                <span className="text-lg font-semibold">
                  {skinToneIcons[girl.skinTone]} {girl.skinTone === 'fair' ? 'ขาวใส' : girl.skinTone === 'light' ? 'ขาวอมเหลือง' : girl.skinTone === 'medium' ? 'น้ำตาลอ่อน' : 'คล้ำ'}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-medium">สถานที่</span>
                <span className="text-lg font-semibold text-blue-600">{girl.location}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600 font-medium">Package</span>
                <span className={`text-lg font-semibold px-3 py-1 rounded-full text-xs ${
                  girl.tier === 'free' 
                    ? 'bg-gray-200 text-gray-700' 
                    : girl.tier === 'premium' 
                    ? 'bg-yellow-200 text-yellow-800' 
                    : girl.tier === 'vip' 
                    ? 'bg-purple-200 text-purple-800' 
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  💎 {girl.tier === 'free' ? 'FREE' : girl.tier?.toUpperCase() || 'FREE'}
                </span>
              </div>
              
              {girl.availability?.workingHours && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600 font-medium">เวลาทำงาน</span>
                  <span className="text-lg font-semibold text-green-600">
                    {girl.availability.workingHours.start} - {girl.availability.workingHours.end}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              💰 ราคาค่าบริการ
            </h3>
            <div className="space-y-4">
              {girl.discountType && girl.discountType !== 'none' && girl.discountValue > 0 ? (
                <div className="space-y-4">
                  {/* ราคาเดิม */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">ราคาเดิม</span>
                      <span className="text-xs text-gray-500">(ก่อนส่วนลด)</span>
                    </div>
                    <span className="text-xl font-semibold text-gray-700">
                      <span className="line-through">
                        {girl.servicePrice || girl.prices?.servicePrice || 1500}
                      </span> บาท/ชม.
                    </span>
                  </div>
                  
                  {/* รายละเอียดส่วนลด */}
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-3 flex items-center">
                      🎯 รายละเอียดส่วนลด
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">ประเภทส่วนลด:</span>
                        <span className="text-sm font-semibold text-yellow-700">
                          {girl.discountType === 'fixed' ? 'ลดเป็นจำนวนเงิน' : 'ลดเป็นเปอร์เซ็นต์'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">ค่าส่วนลด:</span>
                        <span className="text-sm font-semibold text-red-600">
                          {girl.discountType === 'fixed' 
                            ? `${girl.discountValue.toLocaleString()} บาท`
                            : `${girl.discountValue}%`
                          }
                        </span>
                      </div>
                      {girl.discountType === 'percentage' && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">จำนวนเงินที่ลด:</span>
                          <span className="text-sm font-semibold text-red-600">
                            {Math.round(((girl.servicePrice || girl.prices?.servicePrice || 1500) * girl.discountValue) / 100).toLocaleString()} บาท
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* ราคาหลังส่วนลด */}
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">ราคาหลังส่วนลด</span>
                      <span className="text-xs text-green-600">(ราคาสุดท้าย)</span>
                    </div>
                    <span className="text-2xl font-bold text-green-600">
                      {calculateDiscountedPrice(
                        girl.servicePrice || girl.prices?.servicePrice || 1500,
                        girl.discountType,
                        girl.discountValue
                      ).toLocaleString()} บาท/ชม.
                    </span>
                  </div>
                  
                  {/* สรุปการประหยัด */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="text-center">
                      <div className="text-sm text-blue-600 mb-1">💰 ประหยัดได้</div>
                      <div className="text-lg font-bold text-blue-700">
                        {girl.discountType === 'fixed' 
                          ? `${girl.discountValue.toLocaleString()} บาท/ชม.`
                          : `${Math.round(((girl.servicePrice || girl.prices?.servicePrice || 1500) * girl.discountValue) / 100).toLocaleString()} บาท/ชม.`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-gray-600 font-medium">ราคาค่าบริการ</span>
                  <span className="text-2xl font-bold text-green-600">
                    {girl.servicePrice || girl.prices?.servicePrice || 1500} บาท/ชม.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact & Booking Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Contact Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              📱 ข้อมูลติดต่อ
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-gray-600 font-medium">ประเภทการติดต่อ</span>
                <span className="text-lg font-semibold text-blue-600">
                  {girl.lineType === 'self' ? 'LINE น้อง' : girl.lineType === 'modeling' ? 'LINE โมเดลลิ่ง' : 'ไม่แสดง'}
                </span>
              </div>
              
              {girl.lineContact && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-gray-600 font-medium">LINE ID</span>
                  <span className="text-lg font-semibold text-green-600">@{girl.lineContact}</span>
                </div>
              )}
              
              {girl.bio && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-700 mb-2">💬 คำอธิบาย</h4>
                  <p className="text-gray-600">{girl.bio}</p>
                </div>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              📅 จองคิว
            </h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-200">
                <div className="text-3xl mb-2">🎯</div>
                <h4 className="font-bold text-gray-800 mb-2">พร้อมรับงาน</h4>
                <p className="text-sm text-gray-600">
                  {girl.status === 'available' 
                    ? 'น้องพร้อมรับงานและพร้อมให้บริการ' 
                    : 'น้องไม่พร้อมรับงานในขณะนี้'
                  }
                </p>
              </div>
              
              {girl.status === 'available' && !isOwner && (
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      console.log('🎯 จองคิว button clicked!', {
                        girlId: girl.id,
                        girlName: girl.nickname,
                        showBookingModal: showBookingModal
                      });
                      setShowBookingModal(true);
                    }}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-pink-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    🎯 จองคิวเลย
                  </button>
                  
                  {/* ปุ่มให้ของขวัญ - แสดงสำหรับทุกคนยกเว้นตัวเอง */}
                  {currentUser && currentUser.uid !== girl.id && (
                    <div className="mt-3">
                      <GiftButton
                        girlId={girl.id}
                        girlName={girl.nickname}
                        className="w-full"
                      />
                    </div>
                  )}
                  
                  <div className="text-center text-sm text-gray-500">
                    คลิกเพื่อเลือกวันที่และเวลาที่ต้องการ
                  </div>
                  
                  {/* ข้อความแจ้งเตือนทางกฎหมาย */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-blue-500 text-sm">📋</span>
                        <div className="text-xs text-blue-700">
                          <span className="font-semibold">ข้อกำหนดการใช้งาน</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowLegalDisclaimer(!showLegalDisclaimer)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        {showLegalDisclaimer ? 'ซ่อน' : 'ดูรายละเอียด'}
                      </button>
                    </div>
                    
                    {showLegalDisclaimer && (
                      <div className="mt-2 text-xs text-blue-700">
                        การค้าประเวณีเป็นสิ่งผิดกฎหมายในประเทศไทย บริการนี้มีไว้เพื่อการพบปะสังสรรค์และกิจกรรมที่ถูกต้องตามกฎหมายเท่านั้น
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {isOwner && (
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-2xl mb-2">👑</div>
                  <p className="text-blue-600 font-semibold">นี่คือโปรไฟล์ของคุณ</p>
                  <p className="text-sm text-gray-600 mt-1">ไปที่แดชบอร์ดเพื่อจัดการโปรไฟล์</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="text-2xl">⭐</div>
            <h2 className="text-2xl font-bold text-gray-900">รีวิวจากลูกค้า</h2>
          </div>
          
          <ReviewList
            userId={girl.id}
            userRole="girl"
            showStats={true}
            showResponse={false}
            limit={5}
          />
        </div>

        {/* Analytics สำหรับเจ้าของ */}
        {isOwner && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              📊 สถิติการดูรูป
            </h3>
            <ImageAnalytics 
              girlId={girl.id} 
              isOwner={isOwner}
            />
          </div>
        )}

        {/* ของขวัญที่ได้รับ */}
        <div className="mt-6">
          <GiftDisplay girlId={girl.id} />
        </div>

        {/* Booking Modal */}
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            girlData={girl}
            price={girl.coinPrice || girl.prices?.coinPrice || girl.prices?.base || 50}
            onClose={() => setShowBookingModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default GirlProfile;